import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import InputField from "../../../components/ui/form/InputField";
import { Helmet } from "react-helmet";
import Button from "../../../components/ui/utils/Button";
import { HiOutlineKey } from "react-icons/hi";
import { MdPhone, MdBusiness, MdPerson } from "react-icons/md";
import apiRequest from "../../../utils/apiRequest";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { BRAND_NAME } from "../../../utils/constant";
import {
  DASHBOARD_ROUTE,
  HOME_ROUTE,
  LOGIN_ROUTE,
} from "../../../routes/web/rootRoutes";
import logo from "../../../assets/logo.jpg";
import authManager from "../../../utils/authManager";
import { LogoMain } from "../../../utils/helper";

const Register = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const navigate = useNavigate();

  // OTP Refs
  const otpRef0 = useRef(null);
  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRefs = [otpRef0, otpRef1, otpRef2, otpRef3];

  const formik = useFormik({
    initialValues: {
      name: "",
      mobile: "",
      otp: "",
      corporateCode: "",
      password: "",
    },

    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        // STEP 1: SEND OTP
        if (!isOtpSent) {
          if (values.mobile.length !== 10) {
            toast.error("Enter a valid 10-digit mobile number");
            return;
          }

          await apiRequest.sendOtp({
            body: { mobile: values.mobile, type: "register" },
          });

          toast.success("OTP sent successfully!");
          setIsOtpSent(true);
          setTimer(59);
          setServerErrors({});
          return;
        }

        // STEP 2: VERIFY OTP & REGISTER
        const response = await apiRequest.register({
          body: {
            name: values.name,
            mobile: values.mobile,
            otp: values.otp,
            corporateCode: values.corporateCode,
            password: values.password,
          },
        });

        if (response?.data?.response) {
          const data = response.data?.data;

          authManager.setAccessToken(data?.token);
          authManager.setRole(data?.role);

          toast.success("Registration successful!");

          if (data?.role === "Partner") {
            navigate(DASHBOARD_ROUTE);
          } else {
            navigate(HOME_ROUTE);
          }
        }

        setServerErrors({});
      } catch (error) {
        if (error?.status === 400) {
          setServerErrors(error?.response?.data?.data || {});
        }
        toast.error(
          String(error?.response?.data?.message || "Something went wrong")
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  // TIMER
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  // OTP INPUT HANDLER
  const handleOtpChange = (value, index) => {
    const otpArray = formik.values.otp.padEnd(4, " ").split("");
    otpArray[index] = value;
    const newOtp = otpArray.join("").replace(/\s/g, "");
    formik.setFieldValue("otp", newOtp);

    if (value && index < 3) otpRefs[index + 1].current?.focus();
  };

  return (
    <>
      <Helmet>
        <title>User Sign Up - {BRAND_NAME}</title>
      </Helmet>

      <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-blue-50 via-white to-purple-50">

        <div className="hidden md:flex flex-col justify-center items-center w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white relative">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
          <div className="z-10 text-center px-8">
            <img
              src={logo}
              alt="Brand Logo"
              className="mx-auto mb-6 w-28 h-28 rounded-full border-4 border-white/40 shadow-lg"
            />
            <h1 className="text-4xl font-bold mb-3">Welcome to {BRAND_NAME}</h1>
            <p className="text-lg text-white/90">
              Quick registration with OTP verification.
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-md bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl p-8">
            <div className="text-center mb-8">
              <img
                className="h-[100px] rounded-full border-red-500 shadow mx-auto"
                src={LogoMain}
                alt="logo"
              />
              <h2 className="text-3xl font-bold text-gray-800">User Sign Up</h2>
              <p className="text-gray-500 text-sm mt-1">
                Create your account using your mobile number
              </p>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-5">
              <div className="relative">
                <InputField
                  label="Full Name"
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && formik.errors.name}
                  serverError={serverErrors?.name}
                />
                <MdPerson className="absolute right-3 top-9 text-gray-400" />
              </div>

              <div className="relative">
                <label className="text-sm font-medium text-gray-700">
                  Mobile Number
                </label>

                <div className="flex items-center gap-2">
                  <input
                    id="mobile"
                    type="tel"
                    maxLength={10}
                    placeholder="Enter mobile number"
                    value={formik.values.mobile}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="flex-1 border rounded-lg px-3 py-2"
                  />

                  {!isOtpSent && (
                    <button
                      type="button"
                      disabled={formik.values.mobile.length !== 10}
                      onClick={async () => {
                        try {
                          setIsLoading(true);
                          await apiRequest.sendOtp({
                            body: {
                              mobile: formik.values.mobile,
                              type: "register",
                            },
                          });
                          toast.success("OTP sent!");
                          setIsOtpSent(true);
                          setTimer(59);
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.message ||
                            "Error sending OTP"
                          );
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      className={`px-4 py-2 rounded-lg text-white font-semibold ${formik.values.mobile.length === 10
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-gray-300 cursor-not-allowed"
                        }`}
                    >
                      Send OTP
                    </button>
                  )}
                </div>
              </div>

              {/* OTP INPUT */}
              {isOtpSent && (
                <div className="mt-3">
                  <label className="text-sm font-medium text-gray-700 block text-center mb-2">
                    Enter OTP
                  </label>

                  <div className="flex justify-center gap-3">
                    {otpRefs.map((ref, index) => (
                      <input
                        key={index}
                        ref={ref}
                        maxLength={1}
                        type="text"
                        className="w-12 h-12 text-xl text-center border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                        value={formik.values.otp[index] || ""}
                        onChange={(e) =>
                          handleOtpChange(
                            e.target.value.replace(/\D/, ""),
                            index
                          )
                        }
                      />
                    ))}
                  </div>

                  {/* TIMER / RESEND */}
                  <div className="text-center mt-3">
                    {timer > 0 ? (
                      <span className="text-sm text-gray-500">
                        Resend OTP in {timer}s
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="text-sm text-blue-600 hover:underline"
                        onClick={async () => {
                          try {
                            await apiRequest.sendOtp({
                              body: {
                                mobile: formik.values.mobile,
                                type: "register",
                              },
                            });
                            toast.success("OTP resent!");
                            setTimer(59);
                          } catch (err) {
                            toast.error("Failed to resend OTP");
                          }
                        }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="relative">
                <InputField
                  label="Corporate Code"
                  id="corporateCode"
                  type="text"
                  placeholder="Enter corporate code"
                  value={formik.values.corporateCode}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.corporateCode &&
                    formik.errors.corporateCode
                  }
                  serverError={serverErrors?.corporateCode}
                />
                <MdBusiness className="absolute right-3 top-9 text-gray-400" />
              </div>

              <div className="relative">
                <InputField
                  label="Password"
                  id="password"
                  type="password"
                  placeholder="Enter password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && formik.errors.password}
                  serverError={serverErrors?.password}
                />
                <HiOutlineKey className="absolute right-3 top-9 text-gray-400" />
              </div>

              <Button
                type="submit"
                isLoading={isLoading}
                loadingText={
                  isOtpSent ? "Verifying OTP..." : "Sending OTP..."
                }
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow"
              >
                {isOtpSent ? "Verify & Register" : "Get OTP"}
              </Button>
            </form>

            <p className="text-center text-sm text-gray-600 mt-6">
              Already have an account?
              <button
                onClick={() => navigate(LOGIN_ROUTE)}
                className="text-blue-600 hover:underline ml-1 font-semibold"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
